// ShiftManager.js
// Handles shift open/close lifecycle from the Artist Queue view.
// Per spec section 12: Dan opens the shift at the start of the day,
// which enables ticket issuance from the Register view. He closes it
// at the end of the day, which triggers the session summary.
//
// This component renders in two modes:
//   - No active shift: shows event name + artist setup, then "Open Queue"
//   - Active shift: shows elapsed clock + "No More Orders" + "Close Shift"
//
// On close: writes shift_summaries/{shiftId}, updates personal_bests/{artistName},
// then shows a recap card before returning to the open-shift screen.
//
// Props:
//   activeShift   {object|null}  current shift document (or null)
//   onShiftOpen   {function}     called with new shift doc after open
//   onShiftClose  {function}     called with final shift doc after close
//   user          {object}       Firebase auth user (staff only)

import { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, doc, updateDoc, setDoc, getDoc,
  serverTimestamp, query, where, getDocs, onSnapshot, orderBy,
  writeBatch, Timestamp,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { writeAlert } from "./utils/alerts";
import AddressAutocomplete from "./components/AddressAutocomplete";
import ShiftMessagingCard from "./components/ShiftMessagingCard";
import ShiftOrderingControls from "./components/ShiftOrderingControls";
import { ARTIST_TAGS } from "./constants/artistTags";
import { COMBO_ORDER, COMBOS, fmtCents, addOnLabel, unfulfilledAddOns } from "./utils/combos";
import useArtists, { artistSlug } from "./hooks/useArtists";
import { generateJoinCode, normalizeJoinCode, setBoundShift, shiftLabel } from "./utils/shiftScope";
import { BOOTH_PRESETS, presetLabelFor } from "./constants/booths";

import { STAFF_THEME } from "./theme/staff";
const BRAND = STAFF_THEME;

function fmtMs(ms) {
  if (ms == null || ms < 0) return "--:--";
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function fmtDuration(ms) {
  if (ms == null) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

// ── SHIFT REVIEW FLOW ──────────────────────────────────────────────────────
function ShiftReviewFlow({ orders, onDone }) {
  const [idx, setIdx] = useState(0);
  const order = orders[idx];
  const [tags, setTags]       = useState(order?.artistTagsReflection || []);
  const [privacy, setPrivacy] = useState(order?.galleryPrivacy || "public");
  const [saving, setSaving]   = useState(false);

  // Sync local state when navigating between orders
  useEffect(() => {
    if (!orders[idx]) return;
    setTags(orders[idx].artistTagsReflection || []);
    setPrivacy(orders[idx].galleryPrivacy || "public");
  }, [idx, orders]);

  async function toggleTag(tagId) {
    const next = tags.includes(tagId) ? tags.filter(t => t !== tagId) : [...tags, tagId];
    setTags(next);
    await updateDoc(doc(db, "orders", order.id), { artistTagsReflection: next }).catch(() => {});
  }

  async function togglePrivacy() {
    const next = privacy === "public" ? "private" : "public";
    setPrivacy(next);
    await updateDoc(doc(db, "orders", order.id), { galleryPrivacy: next }).catch(() => {});
  }

  if (!order) return null;

  const src  = order.photo_cutout_url || order.photo_url;
  const name = order.subjectName || order.recipientName || order.customerName || "";

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.5rem" }}>
      {/* Progress */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: BRAND.dim, fontSize: "0.75rem" }}>{idx + 1} of {orders.length}</div>
        <div style={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
          {order.queueNumber ? `#${order.queueNumber} · ` : ""}{name}
        </div>
        <button onClick={onDone} style={{ background: "none", border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: BRAND.dim, fontSize: "0.72rem", padding: "0.2rem 0.5rem", cursor: "pointer" }}>Done</button>
      </div>

      {/* Photo */}
      <div style={{ background: "#000", borderRadius: "12px", overflow: "hidden", aspectRatio: "1/1", maxHeight: "280px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {src ? (
          <img src={src} alt={name} style={{ width: "100%", height: "100%", objectFit: "contain", filter: order.photo_cutout_url ? "drop-shadow(0 4px 12px rgba(0,0,0,0.6))" : "none" }} />
        ) : (
          <div style={{ color: BRAND.dim, fontSize: "1.5rem" }}>No photo</div>
        )}
      </div>

      {/* Privacy toggle */}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button onClick={togglePrivacy} style={{
          flex: 1, padding: "0.6rem", borderRadius: "8px", fontWeight: "bold", fontSize: "0.85rem", cursor: "pointer",
          background: privacy === "public" ? "rgba(112,190,68,0.15)" : "rgba(255,255,255,0.05)",
          border: privacy === "public" ? "2px solid rgba(112,190,68,0.5)" : "2px solid rgba(255,255,255,0.1)",
          color: privacy === "public" ? "#70be44" : BRAND.dim,
        }}>👁 Public</button>
        <button onClick={togglePrivacy} style={{
          flex: 1, padding: "0.6rem", borderRadius: "8px", fontWeight: "bold", fontSize: "0.85rem", cursor: "pointer",
          background: privacy === "private" ? "rgba(239,83,126,0.15)" : "rgba(255,255,255,0.05)",
          border: privacy === "private" ? "2px solid rgba(239,83,126,0.5)" : "2px solid rgba(255,255,255,0.1)",
          color: privacy === "private" ? "#ef537e" : BRAND.dim,
        }}>🔒 Private</button>
      </div>

      {/* Vibes */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
        {ARTIST_TAGS.map(tag => {
          const active = tags.includes(tag.id);
          return (
            <button key={tag.id} onClick={() => toggleTag(tag.id)} style={{
              background: active ? "rgba(252,179,31,0.18)" : "rgba(255,255,255,0.05)",
              border: active ? "2px solid rgba(252,179,31,0.6)" : "2px solid rgba(255,255,255,0.1)",
              borderRadius: "10px", padding: "0.3rem 0.55rem",
              cursor: "pointer", display: "flex", alignItems: "center", gap: "4px",
              transition: "all 0.15s ease", transform: active ? "scale(1.06)" : "scale(1)",
            }}>
              <span style={{ fontSize: "1.2rem", lineHeight: 1 }}>{tag.emoji}</span>
              <span style={{ color: active ? "#fcb31f" : "rgba(255,255,255,0.4)", fontSize: "0.7rem", fontWeight: "bold" }}>{tag.label}</span>
            </button>
          );
        })}
      </div>

      {/* Gut feel tags (read-only) */}
      {order.artistTags?.length > 0 && (
        <div style={{ color: BRAND.dim, fontSize: "0.72rem" }}>
          Gut feel: {order.artistTags.map(id => ARTIST_TAGS.find(t => t.id === id)?.emoji || "").join(" ")}
        </div>
      )}

      {/* Nav */}
      <div style={{ display: "flex", gap: "0.75rem" }}>
        <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
          style={{ flex: 1, padding: "0.65rem", borderRadius: "8px", background: idx === 0 ? BRAND.card : BRAND.raised, color: idx === 0 ? BRAND.dim : "white", border: "none", fontSize: "0.9rem", fontWeight: "bold", cursor: idx === 0 ? "default" : "pointer" }}>← Prev</button>
        {idx < orders.length - 1 ? (
          <button onClick={() => setIdx(i => i + 1)}
            style={{ flex: 2, padding: "0.65rem", borderRadius: "8px", background: BRAND.gold, color: BRAND.dark, border: "none", fontSize: "0.9rem", fontWeight: "bold", cursor: "pointer" }}>Next →</button>
        ) : (
          <button onClick={onDone}
            style={{ flex: 2, padding: "0.65rem", borderRadius: "8px", background: BRAND.green, color: "white", border: "none", fontSize: "0.9rem", fontWeight: "bold", cursor: "pointer" }}>✓ Finish Review</button>
        )}
      </div>
    </div>
  );
}

export default function ShiftManager({ activeShift, onShiftOpen, onShiftClose, user }) {
  const [elapsed, setElapsed]           = useState("");
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [pendingAddOnOrders, setPendingAddOnOrders] = useState([]); // unfulfilled combo goods at close
  const [showForceClose, setShowForceClose]     = useState(false);
  const [pendingOrders, setPendingOrders]       = useState([]);
  const [closing, setClosing]           = useState(false);
  const [opening, setOpening]           = useState(false);
  const [shiftRecap, setShiftRecap]     = useState(null);
  const [showReview, setShowReview]     = useState(false);

  // ── PERSISTED OPEN-SHIFT SETTINGS ──────────────────────────────────────────
  const [eventName, setEventNameRaw]   = useState(() => localStorage.getItem("gt_event_name")  || "");
  // Fixed booth code for THIS device's rig. Blank = generate a fresh one each
  // time. Sticky on purpose: an OBS browser source URL has to be typed in
  // BEFORE the shift opens, so a rig that always opens as the same code lets
  // its scene collection be set up once and left alone. See utils/shiftScope.
  const [fixedJoinCode, setFixedJoinCodeRaw] = useState(() => {
    try { return normalizeJoinCode(localStorage.getItem("gt_fixed_join_code") || ""); } catch { return ""; }
  });
  const [venueAddress, setVenueAddressRaw] = useState(() => localStorage.getItem("gt_venue_address") || "");
  // Default to PUBLIC on first run; remembers last choice after that.
  const [venueIsPublic, setVenueIsPublicRaw] = useState(
    () => localStorage.getItem("gt_venue_is_public") !== "false"
  );
  const [artists, setArtistsRaw]       = useState(() => {
    try { return JSON.parse(localStorage.getItem("gt_artists")) || ["Dan"]; }
    catch { return ["Dan"]; }
  });
  const [newArtistInput, setNewArtistInput] = useState("");
  // Artists registry — canonical identity list; roster picker offers these as
  // one-tap chips and free-text adds auto-create entries. Degrades to the old
  // free-text-only behavior when the registry is empty.
  const { artists: registry } = useArtists();
  // Sticky preference: three-way ordering voice for the next shift
  // ("off" | "free" party | "paid" market). Seeds shift.qrFlows.mode at open —
  // previously openShift hardcoded "paid" whenever join was on, so every party
  // shift silently opened in Market voice and scans landed on standby until
  // someone remembered to flip 🎛 Shift Controls (bit us at Metal Brunch
  // 2026-07-26). Migrates from the old boolean sticky on first run.
  const [joinModeDefault, setJoinModeDefaultRaw] = useState(() => {
    const v = localStorage.getItem("gt_default_join_mode");
    if (v === "off" || v === "free" || v === "paid") return v;
    return localStorage.getItem("gt_default_stream_join") === "true" ? "paid" : "off";
  });
  // Brief "✓ /join is now live" toast after enabling Stream Join from the HUD.
  const [streamJoinJustEnabled, setStreamJoinJustEnabled] = useState(false);

  // Sticky preference: Bluesky griddle bot (auto-post finished pancakes to a
  // live shift thread). Default OFF — posting is public and opt-in per shift.
  const [blueskyAutoPost, setBlueskyAutoPostRaw] = useState(
    () => localStorage.getItem("gt_default_bluesky_autopost") === "true"
  );

  // Context mode — controls overlay header variant + marquee filtering.
  // "stream"  : broadcast-only (slim header, stream+both messages)
  // "booth"   : in-person only (billboard header, booth+both messages)
  // "both"    : streaming from the booth (billboard header, all messages)
  // Sticky between shifts; defaults to "stream" on first run for safety
  // (preserves the original overlay look until Dan opts in).
  const [contextMode, setContextModeRaw] = useState(
    () => {
      const v = localStorage.getItem("gt_default_context_mode");
      return v === "stream" || v === "booth" || v === "both" ? v : "stream";
    }
  );
  function setContextMode(v) {
    setContextModeRaw(v);
    localStorage.setItem("gt_default_context_mode", v);
  }

  // ── FLASH DESIGN PROFILES ──────────────────────────────────────────────────
  // All available flashDesignProfiles (for the open-shift multi-select).
  // Selected ids + includeBase toggle are sticky between shifts so the common
  // case ("no overrides today") doesn't require re-toggling on every open.
  const [allProfiles, setAllProfiles] = useState(null);
  const [selectedProfileIds, setSelectedProfileIdsRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gt_selected_flash_profiles")) || []; }
    catch { return []; }
  });
  const [includeBaseFlashSet, setIncludeBaseFlashSetRaw] = useState(
    () => localStorage.getItem("gt_include_base_flash_set") !== "false"
  );
  function setSelectedProfileIds(v) {
    const next = typeof v === "function" ? v(selectedProfileIds) : v;
    setSelectedProfileIdsRaw(next);
    localStorage.setItem("gt_selected_flash_profiles", JSON.stringify(next));
  }
  function setIncludeBaseFlashSet(v) {
    setIncludeBaseFlashSetRaw(v);
    localStorage.setItem("gt_include_base_flash_set", v ? "true" : "false");
  }
  useEffect(() => {
    // Needed both while opening a shift (the multi-select) AND during an
    // active shift (the live flash-set picker in the Messaging modal), so
    // always subscribe.
    const q = query(collection(db, "flashDesignProfiles"), orderBy("name"));
    return onSnapshot(q, snap => {
      setAllProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => setAllProfiles([]));
  }, []);

  // ── TODAY'S BOOKING PRE-FILL ───────────────────────────────────────────────
  // If the CRM has a public booking scheduled for today that hasn't already
  // been linked to a shift, offer a one-tap "Pre-fill from today's event"
  // suggestion above the event-name input. When used, shift.bookingId and
  // booking.shiftId mirror each other so the /events public page (and any
  // future surfaces) can join the two worlds.
  const [todayBooking, setTodayBooking]       = useState(null);
  const [todayBookingCount, setTodayBookingCount] = useState(0);
  const [dismissedBooking, setDismissedBooking] = useState(false);
  const [linkedBookingId, setLinkedBookingId] = useState(null);

  // ── PER-SHIFT OVERLAY MESSAGING TOGGLES ────────────────────────────────────
  // Lets the operator turn off individual marquee messages + booth showcase
  // cards for one shift only (e.g. venue-specific copy that doesn't fit
  // today's event). Buffered in local state during shift-open, then written
  // onto the new shift doc by openShift(). Reset on every mount — overrides
  // are deliberately one-shift-only and do NOT persist between shifts.
  const [pendingDisabledMarqueeTexts, setPendingDisabledMarqueeTexts] = useState([]);
  const [pendingDisabledShowcaseIds,  setPendingDisabledShowcaseIds]  = useState([]);
  // Combo offers active for the next shift. Default = ALL combos: upsell is
  // opt-out, and free/party voice suppresses every combo surface anyway
  // (activeCombosForShift), so party events stay commerce-free without any
  // per-shift fiddling. Event Config profiles can override.
  const [pendingComboKeys, setPendingComboKeys] = useState(COMBO_ORDER);
  // Road Mode pricing block for the next shift — arrives ONLY via an Event
  // Config staging (no open-form editor; the profile is the editor). Null =
  // home prices, and the block dies with the shift when it closes.
  const [pendingPricing, setPendingPricing] = useState(null);
  // Active-shift modal (live edits write straight to Firestore). Hosts both
  // the marquee/showcase toggles and the live flash-set picker.
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  // 🎛 Shift Controls modal — on-screen ordering flows (shift.qrFlows) +
  // scene context. Replaced the standalone "📲 QR Ordering" and context-cycle
  // HUD buttons (2026-07-07).
  const [showControlsModal, setShowControlsModal] = useState(false);
  // Source data for the toggle card — read from Firestore so both the open
  // form and the active-shift modal see the same marquee + showcase items.
  const [streamConfig, setStreamConfig] = useState(null);
  const [showcaseConfig, setShowcaseConfig] = useState(null);
  useEffect(() => {
    return onSnapshot(
      doc(db, "config", "stream"),
      (snap) => setStreamConfig(snap.exists() ? snap.data() : { marqueeMessages: [] }),
      () => setStreamConfig({ marqueeMessages: [] })
    );
  }, []);
  useEffect(() => {
    return onSnapshot(
      doc(db, "config", "boothShowcase"),
      (snap) => setShowcaseConfig(snap.exists() ? snap.data() : { items: [] }),
      () => setShowcaseConfig({ items: [] })
    );
  }, []);

  // ── EVENT CONFIG HANDOFF ───────────────────────────────────────────────────
  // The /event-config screen stages a full shift setup in config/eventSetup
  // ("Apply for next shift"). On mount with no active shift, pre-fill the open
  // form from it — through the existing setters, so device stickies converge
  // to the applied profile. One-shot: openShift() marks the staging consumed,
  // and stagings older than 18h are ignored so last week's setup can't leak
  // into an unrelated shift. Content sessions never touch it.
  const [eventSetupApplied, setEventSetupApplied] = useState(null); // { profileName } | null
  const eventSetupCheckedRef = useRef(false);
  useEffect(() => {
    if (activeShift || eventSetupCheckedRef.current) return;
    eventSetupCheckedRef.current = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "config", "eventSetup"));
        if (!snap.exists()) return;
        const s = snap.data();
        if (s.consumedByShiftId) return;
        const appliedMs = s.appliedAt?.toMillis?.() ?? 0;
        if (!appliedMs || Date.now() - appliedMs > 18 * 3600 * 1000) return;
        const d = s.shiftDefaults || {};
        if (typeof d.eventName === "string")    setEventName(d.eventName);
        if (typeof d.venueAddress === "string") setVenueAddress(d.venueAddress);
        if (typeof d.venueIsPublic === "boolean") setVenueIsPublic(d.venueIsPublic);
        if (Array.isArray(d.artistRoster) && d.artistRoster.length) setArtists(d.artistRoster);
        if (d.joinMode === "off" || d.joinMode === "free" || d.joinMode === "paid") {
          setJoinModeDefault(d.joinMode);
        } else if (typeof d.livestreamJoinEnabled === "boolean") {
          // Older profiles carry only the boolean gate — map to market/off.
          setJoinModeDefault(d.livestreamJoinEnabled ? "paid" : "off");
        }
        if (["stream", "booth", "both"].includes(d.contextMode)) setContextMode(d.contextMode);
        if (Array.isArray(d.activeFlashProfileIds)) setSelectedProfileIds(d.activeFlashProfileIds);
        if (typeof d.includeBaseFlashSet === "boolean") setIncludeBaseFlashSet(d.includeBaseFlashSet);
        if (Array.isArray(d.disabledMarqueeTexts)) setPendingDisabledMarqueeTexts(d.disabledMarqueeTexts);
        if (Array.isArray(d.disabledShowcaseIds))  setPendingDisabledShowcaseIds(d.disabledShowcaseIds);
        if (Array.isArray(d.activeComboKeys)) setPendingComboKeys(d.activeComboKeys);
        if (d.pricing && typeof d.pricing === "object") setPendingPricing(d.pricing);
        setEventSetupApplied({ profileName: s.profileName || null });
      } catch (err) {
        console.warn("ShiftManager: eventSetup prefill failed", err?.message || err);
      }
    })();
  }, [activeShift]); // eslint-disable-line react-hooks/exhaustive-deps

  function togglePendingMarquee(text) {
    setPendingDisabledMarqueeTexts((prev) =>
      prev.includes(text) ? prev.filter((t) => t !== text) : [...prev, text]
    );
  }
  function togglePendingShowcase(id) {
    setPendingDisabledShowcaseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  // Active-shift toggles write directly to the shift doc. Optimistic local
  // state isn't necessary — Firestore's onSnapshot in the parent will
  // re-render with the new arrays inside a few hundred ms, and the modal
  // reads them directly from `activeShift.disabledMarqueeTexts` each render.
  async function toggleActiveMarquee(text) {
    if (!activeShift?.id) return;
    const cur = Array.isArray(activeShift.disabledMarqueeTexts) ? activeShift.disabledMarqueeTexts : [];
    const next = cur.includes(text) ? cur.filter((t) => t !== text) : [...cur, text];
    await updateDoc(doc(db, "shifts", activeShift.id), { disabledMarqueeTexts: next })
      .catch((err) => console.warn("Failed to toggle marquee:", err?.message));
  }
  async function toggleActiveShowcase(id) {
    if (!activeShift?.id) return;
    const cur = Array.isArray(activeShift.disabledShowcaseIds) ? activeShift.disabledShowcaseIds : [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    await updateDoc(doc(db, "shifts", activeShift.id), { disabledShowcaseIds: next })
      .catch((err) => console.warn("Failed to toggle showcase:", err?.message));
  }

  // Live flash-set toggles — write the same fields openShift() stamps, so
  // useFlashDesigns(activeShift) re-renders every overlay within a frame. This
  // is the ONLY way to change flash sets mid-shift; Event Config Apply only
  // stages config/eventSetup for the NEXT shift-open, never a live shift.
  async function toggleActiveFlashProfile(id) {
    if (!activeShift?.id) return;
    const cur = Array.isArray(activeShift.activeFlashProfileIds) ? activeShift.activeFlashProfileIds : [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    await updateDoc(doc(db, "shifts", activeShift.id), { activeFlashProfileIds: next })
      .catch((err) => console.warn("Failed to toggle flash profile:", err?.message));
  }
  async function setActiveBaseFlashSet(on) {
    if (!activeShift?.id) return;
    await updateDoc(doc(db, "shifts", activeShift.id), { includeBaseFlashSet: on })
      .catch((err) => console.warn("Failed to toggle base flash set:", err?.message));
  }

  function setEventName(v) { setEventNameRaw(v); localStorage.setItem("gt_event_name", v); }
  function setFixedJoinCode(v) {
    const norm = normalizeJoinCode(v);
    setFixedJoinCodeRaw(norm);
    try { localStorage.setItem("gt_fixed_join_code", norm); } catch { /* storage blocked */ }
  }
  function setVenueAddress(v) { setVenueAddressRaw(v); localStorage.setItem("gt_venue_address", v); }
  function setVenueIsPublic(v) { setVenueIsPublicRaw(v); localStorage.setItem("gt_venue_is_public", v ? "true" : "false"); }
  function setArtists(v) {
    const next = typeof v === "function" ? v(artists) : v;
    setArtistsRaw(next);
    localStorage.setItem("gt_artists", JSON.stringify(next));
    // The "storage" event never fires in the SAME document, so the queue view's
    // start-scrim buttons went stale until reload. This custom event closes it.
    window.dispatchEvent(new Event("gt-artists-changed"));
  }
  function setJoinModeDefault(v) {
    setJoinModeDefaultRaw(v);
    localStorage.setItem("gt_default_join_mode", v);
    // Legacy mirror — Event Config's sticky-seed still reads the boolean.
    localStorage.setItem("gt_default_stream_join", v !== "off" ? "true" : "false");
  }
  function setBlueskyAutoPost(v) {
    setBlueskyAutoPostRaw(v);
    localStorage.setItem("gt_default_bluesky_autopost", v ? "true" : "false");
  }

  function addArtist(rawName) {
    const name = (typeof rawName === "string" ? rawName : newArtistInput).trim();
    if (!name || artists.includes(name)) { setNewArtistInput(""); return; }
    setArtists(a => [...a, name]);
    setNewArtistInput("");
    // Auto-formalize: a typed name that isn't in the artists registry becomes a
    // registry entry, so attribution/XP tether from their very first pancake.
    const known = registry.some(a => a.name.trim().toLowerCase() === name.toLowerCase()
      || (a.aliases || []).some(al => al.trim().toLowerCase() === name.toLowerCase()));
    if (!known) {
      setDoc(doc(db, "artists", artistSlug(name)), {
        name, email: null, aliases: [], active: true, addedAt: Date.now(),
      }, { merge: true }).catch(() => {});
    }
  }

  function removeArtist(name) {
    setArtists(a => a.filter(x => x !== name));
    // If removed artist is active, fall back to first remaining
    const cur = localStorage.getItem("gt_active_artist");
    if (cur === name) {
      const remaining = artists.filter(x => x !== name);
      localStorage.setItem("gt_active_artist", remaining[0] ?? "");
    }
  }

  // ── FETCH TODAY'S PUBLIC BOOKING (ONCE) ────────────────────────────────────
  // Runs when no shift is active. Pulls a wide ±24h window on confirmedDate,
  // then filters client-side to bookings whose actual local calendar day
  // (confirmedStart preferred, confirmedDate fallback) matches today. This
  // is tolerant of legacy bookings whose confirmedDate was filed against
  // UTC midnight instead of Chicago midnight (off-by-one-day bug fixed on
  // the import side 2026-05-16). Skips bookings already linked to a shift.
  useEffect(() => {
    if (activeShift) return;
    if (linkedBookingId) return; // already linked; don't re-query
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd   = new Date(todayStart.getTime() + 24 * 3600 * 1000);
        // Widen the confirmedDate query by 1 day each side to also catch
        // bookings whose confirmedDate lands on the wrong UTC day. Final
        // day-match decision happens client-side using confirmedStart.
        const winStart = new Date(todayStart.getTime() - 24 * 3600 * 1000);
        const winEnd   = new Date(todayEnd.getTime()   + 24 * 3600 * 1000);
        const snap = await getDocs(query(
          collection(db, "bookings"),
          where("confirmedDate", ">=", Timestamp.fromDate(winStart)),
          where("confirmedDate", "<",  Timestamp.fromDate(winEnd)),
        ));
        if (cancelled) return;
        const todayKey = `${todayStart.getFullYear()}-${todayStart.getMonth()}-${todayStart.getDate()}`;
        const isToday = (b) => {
          // Prefer confirmedStart (precise moment); fall back to confirmedDate.
          const ms = b.confirmedStart?.toMillis?.() ?? b.confirmedDate?.toMillis?.() ?? 0;
          if (!ms) return false;
          const d = new Date(ms);
          return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` === todayKey;
        };
        const rows = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          // Was gated on isPublicEvent; relaxed so private/confirmed bookings
          // also offer prefill (most booth work is private events). The isToday
          // filter still requires a real confirmedStart/confirmedDate, so raw
          // inquiries (inquiryEventDate only) are naturally excluded.
          .filter(b => !b.archivedAt && !b.shiftId)
          .filter(b => !["Declined", "Canceled", "Cancelled"].includes(b.status || ""))
          .filter(isToday);
        // Time-of-day disambiguation. Multiple bookings can share a calendar
        // day; "earliest of day" picks wrong when staff opens an afternoon
        // shift. Prefer the booking that's currently in progress (started,
        // within end + grace), then the next upcoming by start time, and
        // fall back to earliest-of-day only when neither applies (e.g. all
        // events already ended yesterday-grace, or times missing).
        const startMs = (b) => b.confirmedStart?.toMillis?.() ?? b.confirmedDate?.toMillis?.() ?? 0;
        const endMs   = (b) => b.confirmedEnd?.toMillis?.() ?? startMs(b);
        const GRACE_MS = 90 * 60 * 1000;
        const nowMs = Date.now();
        const inProgress = rows.find(b => {
          const s = startMs(b);
          return s && s <= nowMs && nowMs < endMs(b) + GRACE_MS;
        });
        const sortedByStart = [...rows].sort((a, b) => startMs(a) - startMs(b));
        const upcoming = sortedByStart.find(b => startMs(b) > nowMs);
        const picked = inProgress || upcoming || sortedByStart[0] || null;
        setTodayBooking(picked);
        setTodayBookingCount(rows.length);
      } catch (err) {
        // Missing Firestore index or rules mismatch — fail quiet; the form
        // still works as a plain manual open.
        console.warn("ShiftManager: today-booking lookup failed", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeShift, linkedBookingId]);

  function prefillFromTodayBooking() {
    if (!todayBooking) return;
    const ev   = (todayBooking.eventName || "").trim();
    const addr = (todayBooking.venueAddress || "").trim();
    if (ev)   setEventName(ev);
    if (addr) setVenueAddress(addr);
    // Public booking → public venue. Dan can still flip it back if it's a
    // private address we don't want surfaced on keepsakes.
    setVenueIsPublic(true);
    setLinkedBookingId(todayBooking.id);
    // Auto-load this booking's default flash design profile if one is set.
    // Adds to (doesn't replace) any sticky selection from prior shifts so
    // Dan can stack a client set on top of the usual rotation. Dedupe.
    if (todayBooking.defaultFlashProfileId) {
      setSelectedProfileIds(prev => Array.from(new Set([...prev, todayBooking.defaultFlashProfileId])));
    }
  }

  // ── AUTO-CLOSE AFTER 4HR INACTIVITY ────────────────────────────────────────
  // Checks every 5 minutes. Fires if: shift is active, no orders in any active
  // state (drawing/cooking/flipping), and no queue interaction for >4 hours.
  // Shows a prompt rather than auto-closing — never fires mid-cook.
  // "lastActivityAt" is written to the shift doc by ArtistQueueView on state transitions.
  const [showAutoClosePrompt, setShowAutoClosePrompt] = useState(false);
  useEffect(() => {
    if (!activeShift) return;
    const check = setInterval(async () => {
      if (!activeShift?.isActive) return;
      const lastActivity = activeShift.lastActivityAt?.toMillis?.() ?? activeShift.openedAt?.toMillis?.();
      if (lastActivity == null) return; // shift timestamps not synced from server yet
      if (Date.now() - lastActivity < 4 * 60 * 60 * 1000) return; // < 4hr — do nothing
      // Check for any actively in-progress orders before prompting
      const activeSnap = await getDocs(query(
        collection(db, "orders"),
        where("shiftId", "==", activeShift.id),
        where("state", "in", ["drawing", "cooking", "flipping"])
      )).catch(() => null);
      if (activeSnap && activeSnap.docs.some(d => !d.data().trashedAt)) return; // artist actively working — do not prompt
      // Show prompt instead of auto-closing
      setShowAutoClosePrompt(true);
    }, 5 * 60 * 1000);
    return () => clearInterval(check);
  }, [activeShift?.id, activeShift?.isActive, activeShift?.lastActivityAt, activeShift?.openedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ELAPSED CLOCK ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeShift) return;
    const interval = setInterval(() => {
      const openedAt = activeShift.openedAt?.toDate?.() ?? new Date();
      const diffMs = Date.now() - openedAt.getTime();
      const h = Math.floor(diffMs / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      const s = Math.floor((diffMs % 60000) / 1000);
      setElapsed(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [activeShift]);

  // ── OPEN SHIFT ─────────────────────────────────────────────────────────────
  async function openShift() {
    setOpening(true);
    try {
      // Concurrent-events scoping (2026-09-25). Every shift carries a short
      // joinCode so guests and OBS browser sources can name WHICH queue they
      // mean — the on-screen QR encodes it, the /join booth picker falls back
      // to it. Generated against the codes currently in play so two live
      // booths can never share one. See utils/shiftScope.js.
      let takenCodes = [];
      try {
        const activeSnap = await getDocs(query(collection(db, "shifts"), where("isActive", "==", true)));
        takenCodes = activeSnap.docs.map(d => d.data()?.joinCode).filter(Boolean);
      } catch {
        // A failed read must not block opening a queue — a duplicate code is
        // recoverable (the picker still disambiguates by event name).
      }
      // A device's fixed code wins unless another live booth already has it —
      // two booths sharing a code would send scans to the wrong queue, which
      // is the exact failure this whole mechanism exists to prevent.
      const wanted = normalizeJoinCode(fixedJoinCode);
      const joinCode = (wanted.length === 4 && !takenCodes.map(c => String(c).toUpperCase()).includes(wanted))
        ? wanted
        : generateJoinCode(takenCodes);
      if (wanted.length === 4 && joinCode !== wanted) {
        alert(`Booth code ${wanted} is already in use by the other live queue.\nThis booth opened as ${joinCode} instead — update this rig's OBS browser source URLs to ?s=${joinCode}.`);
      }

      const shiftDoc = {
        openedAt:      serverTimestamp(),
        closedAt:      null,
        queueClosedAt: null,
        isActive:      true,
        isQueueOpen:   true,
        // Guest-facing scope handles. joinLabel is what the booth picker
        // shows — defaults to the event name, but stays overridable so a
        // private wedding isn't named on a public picker.
        joinCode,
        joinLabel:     eventName.trim() || null,
        livestreamJoinEnabled: joinModeDefault !== "off",
        // QR-flow model (utils/qrFlow.js) — seeded from the three-way sticky
        // (off / party / market) so a party shift OPENS in party voice and
        // scans go straight to the main queue; the artist can still flip modes
        // live from 🎛 Shift Controls. Writers must keep
        // livestreamJoinEnabled mirrored (qrFlowsWritePatch).
        qrFlows: {
          mode: joinModeDefault,
          customUrl: null,
          scenes: {},
          summonEnabled: true,
        },
        contextMode:           contextMode,
        staffEmail:    user.email,
        eventName:     eventName.trim() || null,
        venueAddress:  venueAddress.trim() || null,
        venueIsPublic: !!venueIsPublic,
        artistRoster:  artists,
        // Link back to the CRM booking if this shift was pre-filled from
        // today's calendar — enables /events past-tail, client recaps, etc.
        bookingId:     linkedBookingId || null,
        // Flash design set selection — empty array + true base flag = today's
        // default. Resolved at order-time by useFlashDesigns(activeShift).
        activeFlashProfileIds: selectedProfileIds,
        includeBaseFlashSet:   includeBaseFlashSet,
        // Combo offers live this shift — consumed by activeCombosForShift()
        // (overlay spotlight, /join interstitial visibility). Free voice
        // suppresses combos regardless of this list.
        activeComboKeys:       pendingComboKeys,
        // Road Mode: per-event pricing (null = home prices). Stamped from the
        // Event Config staging; server checkouts + every price display read
        // it off THIS shift doc, so closing the shift reverts everything.
        pricing:               pendingPricing || null,
        // Bluesky griddle bot — each finished pancake posts into a live shift
        // thread. Explicit boolean (never undefined) so the trigger's
        // config-level default only applies to pre-toggle shifts.
        blueskyAutoPost:       !!blueskyAutoPost,
        // Propagate the test-data flag if we pre-filled from a seeded test
        // booking. Keeps cleanTestData() able to sweep the shift in the
        // same pass as its parent booking.
        ...(todayBooking?._isTestData ? { _isTestData: true } : {}),
        // Per-shift overlay overrides — populated from the messaging card
        // in the open form. Arrays of strings; empty = nothing disabled.
        disabledMarqueeTexts: pendingDisabledMarqueeTexts,
        disabledShowcaseIds:  pendingDisabledShowcaseIds,
        // Stats — all computed on close
        ordersCompleted:           0,
        ordersRejected:            0,
        ordersExpired:             0,
        flashCount:                0,
        customCount:               0,
        photosCaptured:            0,
        finishedWithoutPhotoCount: 0,
        avgDrawingTimeMs:          null,
        avgCookTimeMs:             null,
        avgTotalTimeMs:            null,
        fastestDrawingTimeMs:      null,
        fastestDrawingOrderId:     null,
        longestDrawingTimeMs:      null,
        peakQueueDepth:            0,
        busiestHour:               null,
        forceClose:                false,
        // Live queue state — read by customer confirmed screens
        currentNumber:             0,
        lastNumberIssued:          0,
      };

      const shiftRef = await addDoc(collection(db, "shifts"), shiftDoc);

      // The device that opened a queue IS working that queue — bind it before
      // anything else can resolve ambiguously. Without this, opening a second
      // concurrent shift would immediately prompt the operator to pick the
      // queue they just created.
      setBoundShift(shiftRef.id, shiftLabel({ id: shiftRef.id, ...shiftDoc }));

      await addDoc(
        collection(db, "shifts", shiftRef.id, "meta"),
        { id: "counter", lastNumber: 0 }
      );

      // Mirror the link onto the booking so CRM can show "View shift →" and
      // /events can join past-tail galleries. Fire-and-forget — a failed
      // write here shouldn't block the shift from opening.
      if (linkedBookingId) {
        updateDoc(doc(db, "bookings", linkedBookingId), {
          shiftId:   shiftRef.id,
          updatedAt: serverTimestamp(),
        }).catch((err) => console.warn("Failed to mirror shiftId onto booking:", err?.message || err));
      }

      // Broadcast live-shift status to the public /events page. Anonymous
      // visitors subscribe to this one doc for the real-time "cooking now"
      // badge. Fire-and-forget — a failure here shouldn't block the shift.
      setDoc(doc(db, "config", "liveShift"), {
        bookingId:   linkedBookingId || null,
        shiftId:     shiftRef.id,
        eventName:   eventName.trim() || null,
        isLive:      true,
        openedAt:    serverTimestamp(),
        contextMode: contextMode,
      }).catch(err => console.warn("liveShift write failed:", err?.message));

      // Mark the Event Config staging consumed so it can't re-apply to a
      // later, unrelated shift. Fire-and-forget.
      if (eventSetupApplied) {
        setDoc(doc(db, "config", "eventSetup"), { consumedByShiftId: shiftRef.id }, { merge: true })
          .catch(err => console.warn("eventSetup consume failed:", err?.message));
      }

      onShiftOpen({ id: shiftRef.id, ...shiftDoc });
      writeAlert("shift_started", {}).catch(() => {});
    } catch (err) {
      console.error("Failed to open shift:", err);
      alert("Could not open shift. Check connection and try again.");
    }
    setOpening(false);
  }

  // ── OPEN CONTENT SESSION ───────────────────────────────────────────────────
  // One-tap shift creation for non-customer content shoots (video, livestream
  // requests, freeform experimentation). Auto-named "Content #N — Month D, YYYY"
  // where N is the running count of prior content sessions. Skips event name,
  // venue, booking link, artist roster — pancakes made in the session inherit
  // the shift id and naturally surface as their own tab on the Outro overlay.
  //
  // Flag on the shift doc: `_isContentSession: true`. NOT `_isHistorical`
  // (that's a separate concept for the legacy archive bucket — see
  // HistoricalUploadModal.jsx).
  async function openContentSession() {
    setOpening(true);
    try {
      // Count prior content sessions to assign the next sequence number.
      const priorSnap = await getDocs(
        query(collection(db, "shifts"), where("_isContentSession", "==", true))
      );
      const seq = priorSnap.size + 1;
      const dateLabel = new Date().toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
      });
      const autoName = `Content #${seq} — ${dateLabel}`;

      const shiftDoc = {
        openedAt:      serverTimestamp(),
        closedAt:      null,
        queueClosedAt: null,
        isActive:      true,
        isQueueOpen:   true,
        livestreamJoinEnabled: false,
        contextMode:           "stream",
        staffEmail:    user.email,
        eventName:     autoName,
        venueAddress:  null,
        venueIsPublic: false,
        // Strings, matching openShift — the object form here was a shape
        // mismatch that made RecapPage drop roster names on normal shifts.
        artistRoster:  ["Dan"],
        bookingId:     null,
        _isContentSession: true,
        // Stats — all computed on close (same scaffold as openShift)
        ordersCompleted:           0,
        ordersRejected:            0,
        ordersExpired:             0,
        flashCount:                0,
        customCount:               0,
        photosCaptured:            0,
        finishedWithoutPhotoCount: 0,
        avgDrawingTimeMs:          null,
        avgCookTimeMs:             null,
        avgTotalTimeMs:            null,
        fastestDrawingTimeMs:      null,
        fastestDrawingOrderId:     null,
        longestDrawingTimeMs:      null,
        peakQueueDepth:            0,
        busiestHour:               null,
        forceClose:                false,
        currentNumber:             0,
        lastNumberIssued:          0,
      };

      const shiftRef = await addDoc(collection(db, "shifts"), shiftDoc);
      await addDoc(
        collection(db, "shifts", shiftRef.id, "meta"),
        { id: "counter", lastNumber: 0 }
      );

      setDoc(doc(db, "config", "liveShift"), {
        bookingId:   null,
        shiftId:     shiftRef.id,
        eventName:   autoName,
        isLive:      true,
        openedAt:    serverTimestamp(),
        contextMode: "stream",
      }).catch(err => console.warn("liveShift write failed:", err?.message));

      onShiftOpen({ id: shiftRef.id, ...shiftDoc });
      writeAlert("shift_started", {}).catch(() => {});
    } catch (err) {
      console.error("Failed to open content session:", err);
      alert("Could not open content session. Check connection and try again.");
    }
    setOpening(false);
  }

  // ── LIVE-SHIFT BADGE HANDOFF ───────────────────────────────────────────────
  // config/liveShift drives the public /events "cooking now" badge. It is one
  // doc, but since 2026-09-25 several shifts can be live at once. On close we
  // hand the badge to a surviving shift instead of blanking it; only the last
  // booth standing turns it off. Public /events still shows one event at a
  // time — showing all of them needs a per-shift collection + a publicEvents
  // builder change, which is parked (see CLAUDE.md).
  async function releaseLiveShift(closingShiftId) {
    const ref = doc(db, "config", "liveShift");
    const cur = await getDoc(ref);
    // Another booth already owns the badge — leave it alone.
    if (cur.exists() && cur.data()?.shiftId && cur.data().shiftId !== closingShiftId) return;

    const stillOpen = await getDocs(query(collection(db, "shifts"), where("isActive", "==", true)));
    const successor = stillOpen.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .find(s => s.id !== closingShiftId && !s._isTestData);

    if (successor) {
      await setDoc(ref, {
        bookingId:   successor.bookingId || null,
        shiftId:     successor.id,
        eventName:   successor.eventName || null,
        isLive:      true,
        openedAt:    successor.openedAt || serverTimestamp(),
        contextMode: successor.contextMode || "market",
      });
      return;
    }

    await setDoc(ref, {
      bookingId: null, shiftId: null, eventName: null,
      isLive: false, openedAt: null,
    });
  }

  // ── CLOSE QUEUE (No More Orders) ───────────────────────────────────────────
  async function closeQueue() {
    if (!activeShift) return;
    await updateDoc(doc(db, "shifts", activeShift.id), {
      isQueueOpen:   false,
      queueClosedAt: serverTimestamp(),
    });
    writeAlert("queue_full", {}).catch(() => {});
  }

  // ── REOPEN QUEUE ───────────────────────────────────────────────────────────
  async function reopenQueue() {
    if (!activeShift) return;
    await updateDoc(doc(db, "shifts", activeShift.id), {
      isQueueOpen:   true,
      queueClosedAt: null,
    });
    writeAlert("queue_open", {}).catch(() => {});
  }

  // ── CLOSE SHIFT ────────────────────────────────────────────────────────────
  async function beginCloseShift() {
    const q = query(
      collection(db, "orders"),
      where("shiftId", "==", activeShift.id),
      where("state", "in", ["queued", "drawing", "flipping", "suspended"])
    );
    const snap = await getDocs(q);
    const unresolved = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.trashedAt);
    setPendingOrders(unresolved);
    // Unfulfilled combo add-ons — goods someone paid for that haven't changed
    // hands. Lives mostly on done/ready orders, which the unresolved query
    // above deliberately excludes, so it's a second sweep. Firestore can't
    // filter inside the array; fetch by state and filter client-side.
    try {
      const addOnSnap = await getDocs(query(
        collection(db, "orders"),
        where("shiftId", "==", activeShift.id),
        where("state", "in", ["done", "ready_to_deliver", "cooking"])
      ));
      const owed = addOnSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(o => !o.trashedAt && unfulfilledAddOns(o).length > 0);
      // Unresolved-state orders with add-ons are already in the first list —
      // include their obligations too so the modal shows every owed item.
      setPendingAddOnOrders([...owed, ...unresolved.filter(o => unfulfilledAddOns(o).length > 0)]);
    } catch {
      setPendingAddOnOrders([]);
    }
    setShowCloseConfirm(true);
  }

  async function closeShift(force = false) {
    if (!activeShift) return;
    setClosing(true);

    try {
      const batch = writeBatch(db);

      if (force && pendingOrders.length > 0) {
        for (const order of pendingOrders) {
          batch.update(doc(db, "orders", order.id), {
            state:      "rejected",
            rejectedAt: serverTimestamp(),
            rejectNote: "event ended — shift force closed",
          });
        }
      }

      // ── Fetch all done orders for this shift ──────────────────────────────
      const completedSnap = await getDocs(query(
        collection(db, "orders"),
        where("shiftId", "==", activeShift.id),
        where("state", "==", "done")
      ));
      // Exclude trashed orders from the shift summary — they're not part of the
      // shift's legitimate output; they're scheduled for deletion.
      const completedWithIds = completedSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.trashedAt);
      const completed = completedWithIds;

      const drawTimes  = completed.map(o => o.drawingTimeMs).filter(t => t != null && t > 0);
      const cookTimes  = completed.map(o => o.cookTimeMs).filter(t => t != null && t > 0);
      const totalTimes = completed
        .filter(o => o.startedAt && o.completedAt)
        .map(o => o.completedAt.toMillis() - o.startedAt.toMillis());

      // ── Per-artist breakdown ──────────────────────────────────────────────
      const artistStats = {};
      for (const o of completed) {
        const name = o.artistName || "Unknown";
        if (!artistStats[name]) {
          artistStats[name] = {
            totalOrders: 0, flashCount: 0, customCount: 0,
            bestCustomDrawMs: null, bestCustomTotalMs: null,
            bestFlashDrawMs: null, bestFlashTotalMs: null,
            drawTimesSum: 0, drawTimesCount: 0,
          };
        }
        const a = artistStats[name];
        a.totalOrders++;
        const isFlash  = o.orderType !== "custom";
        const isCustom = o.orderType === "custom";
        if (isFlash)  a.flashCount++;
        if (isCustom) a.customCount++;

        const drawMs  = o.drawingTimeMs ?? null;
        const cookMs  = o.cookTimeMs   ?? null;
        const totalMs = drawMs != null && cookMs != null ? drawMs + cookMs : null;

        if (drawMs != null) {
          a.drawTimesSum   += drawMs;
          a.drawTimesCount += 1;
          if (isCustom) {
            if (a.bestCustomDrawMs == null || drawMs < a.bestCustomDrawMs) a.bestCustomDrawMs = drawMs;
            if (totalMs != null && (a.bestCustomTotalMs == null || totalMs < a.bestCustomTotalMs)) a.bestCustomTotalMs = totalMs;
          } else {
            if (a.bestFlashDrawMs == null  || drawMs < a.bestFlashDrawMs)  a.bestFlashDrawMs  = drawMs;
            if (totalMs != null && (a.bestFlashTotalMs  == null || totalMs < a.bestFlashTotalMs))  a.bestFlashTotalMs  = totalMs;
          }
        }
      }

      // Finalize artist stats (replace running sums with averages)
      for (const name of Object.keys(artistStats)) {
        const a = artistStats[name];
        a.avgDrawMs = a.drawTimesCount > 0 ? Math.round(a.drawTimesSum / a.drawTimesCount) : null;
        delete a.drawTimesSum;
        delete a.drawTimesCount;
      }

      // ── Base shift stats ──────────────────────────────────────────────────
      const stats = {
        isActive:                  false,
        isQueueOpen:               false,
        closedAt:                  serverTimestamp(),
        forceClose:                force,
        ordersCompleted:           completed.length,
        ordersRejected:            pendingOrders.filter(o => o.state !== "suspended").length + (force ? pendingOrders.length : 0),
        flashCount:                completed.filter(o => o.orderType !== "custom").length,
        customCount:               completed.filter(o => o.orderType === "custom").length,
        photosCaptured:            completed.filter(o => o.photo_url).length,
        finishedWithoutPhotoCount: completed.filter(o => !o.photo_url).length,
        avgDrawingTimeMs:          drawTimes.length ? Math.round(drawTimes.reduce((a, b) => a + b, 0) / drawTimes.length) : null,
        avgCookTimeMs:             cookTimes.length ? Math.round(cookTimes.reduce((a, b) => a + b, 0) / cookTimes.length) : null,
        avgTotalTimeMs:            totalTimes.length ? Math.round(totalTimes.reduce((a, b) => a + b, 0) / totalTimes.length) : null,
        fastestDrawingTimeMs:      drawTimes.length ? Math.min(...drawTimes) : null,
        longestDrawingTimeMs:      drawTimes.length ? Math.max(...drawTimes) : null,
        artistBreakdown:           artistStats,
      };

      batch.update(doc(db, "shifts", activeShift.id), stats);
      await batch.commit();

      // ── Write shift summary doc ───────────────────────────────────────────
      const durationMs = activeShift.openedAt?.toMillis
        ? Date.now() - activeShift.openedAt.toMillis()
        : null;

      await setDoc(doc(db, "shift_summaries", activeShift.id), {
        shiftId:       activeShift.id,
        eventName:     activeShift.eventName ?? null,
        openedAt:      activeShift.openedAt  ?? null,
        closedAt:      serverTimestamp(),
        durationMs,
        totalOrders:   completed.length,
        flashCount:    stats.flashCount,
        customCount:   stats.customCount,
        forceClose:    force,
        artistBreakdown: artistStats,
        bestDrawMs:    drawTimes.length ? Math.min(...drawTimes) : null,
        bestTotalMs:   totalTimes.length ? Math.min(...totalTimes) : null,
        staffEmail:    user.email,
        // Carry linkage + test-data flag over from the live shift doc so
        // /events past-tail (Phase 2) and cleanTestData() can see them.
        bookingId:     activeShift.bookingId ?? null,
        ...(activeShift._isTestData ? { _isTestData: true } : {}),
      });

      // ── Update personal bests per artist ──────────────────────────────────
      for (const [artistName, a] of Object.entries(artistStats)) {
        const pbRef  = doc(db, "personal_bests", artistName);
        const pbSnap = await getDoc(pbRef);
        const pb     = pbSnap.exists() ? pbSnap.data() : {};
        const updates = { artistName, updatedAt: serverTimestamp() };

        if (a.bestCustomDrawMs  != null && (pb.bestCustomDrawMs  == null || a.bestCustomDrawMs  < pb.bestCustomDrawMs))  updates.bestCustomDrawMs  = a.bestCustomDrawMs;
        if (a.bestCustomTotalMs != null && (pb.bestCustomTotalMs == null || a.bestCustomTotalMs < pb.bestCustomTotalMs)) updates.bestCustomTotalMs = a.bestCustomTotalMs;
        if (a.bestFlashDrawMs   != null && (pb.bestFlashDrawMs   == null || a.bestFlashDrawMs   < pb.bestFlashDrawMs))   updates.bestFlashDrawMs   = a.bestFlashDrawMs;
        if (a.bestFlashTotalMs  != null && (pb.bestFlashTotalMs  == null || a.bestFlashTotalMs  < pb.bestFlashTotalMs))  updates.bestFlashTotalMs  = a.bestFlashTotalMs;

        if (Object.keys(updates).length > 2) {
          await setDoc(pbRef, updates, { merge: true });
        }
      }

      // ── Build recap ───────────────────────────────────────────────────────
      const recap = {
        shiftId:      activeShift.id,
        eventName:    activeShift.eventName ?? null,
        closedAt:     new Date(),
        durationMs,
        totalOrders:  completed.length,
        flashCount:   stats.flashCount,
        customCount:  stats.customCount,
        artistStats,
        bestDrawMs:   stats.fastestDrawingTimeMs,
        reviewOrders: completedWithIds.filter(o => o.photo_url || o.photo_cutout_url),
      };

      const finalShift = { ...activeShift, ...stats };
      onShiftClose(finalShift);
      setShiftRecap(recap);
      writeAlert("shift_ended", {}).catch(() => {});

      // Clear the live-shift indicator so the public /events page drops the
      // "cooking now" badge. Same fire-and-forget pattern as the open write.
      //
      // config/liveShift is a SINGLETON but shifts are no longer exclusive
      // (2026-09-25). Closing one booth must not blank the badge for another
      // that is still cooking — so only clear when this shift owns the doc,
      // and hand it off to a surviving shift rather than clearing it.
      releaseLiveShift(activeShift.id).catch(err => console.warn("liveShift release failed:", err?.message));

    } catch (err) {
      console.error("Failed to close shift:", err);
      alert("Could not close shift. Try again.");
    }

    setClosing(false);
    setShowCloseConfirm(false);
    setShowForceClose(false);
  }

  // ── RECAP CARD ─────────────────────────────────────────────────────────────
  // Duration correction state
  const [durationEdit, setDurationEdit] = useState(null); // { hours, minutes } or null
  const [durationSaving, setDurationSaving] = useState(false);
  const [durationSaved, setDurationSaved] = useState(false);

  if (shiftRecap && !activeShift) {
    const multiArtist = Object.keys(shiftRecap.artistStats).length > 1;
    return (
      <div style={{ padding: "2rem 1.5rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem", maxWidth: "420px", margin: "0 auto" }}>
        <div style={{ fontSize: "2.5rem" }}>🎉</div>
        <div style={{ color: "white", fontSize: "1.3rem", fontWeight: "bold", textAlign: "center" }}>That's a wrap!</div>
        {shiftRecap.eventName && <div style={{ color: BRAND.gold, fontSize: "0.9rem" }}>{shiftRecap.eventName}</div>}
        <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>
          {shiftRecap.closedAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          {shiftRecap.durationMs != null ? ` · ${durationSaved ? fmtDuration((durationEdit?.hours ?? 0) * 3600000 + (durationEdit?.minutes ?? 0) * 60000) : fmtDuration(shiftRecap.durationMs)}` : ""}
        </div>

        {/* Duration correction */}
        {!durationEdit && !durationSaved && (
          <button onClick={() => {
            const h = shiftRecap.durationMs != null ? Math.floor(shiftRecap.durationMs / 3600000) : 0;
            const m = shiftRecap.durationMs != null ? Math.floor((shiftRecap.durationMs % 3600000) / 60000) : 0;
            setDurationEdit({ hours: h, minutes: m });
          }} style={{ color: BRAND.dim, fontSize: "0.75rem", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
            Correct duration
          </button>
        )}
        {durationEdit && !durationSaved && (
          <div style={{ background: BRAND.card, borderRadius: "10px", padding: "0.75rem", width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div style={{ color: BRAND.muted, fontSize: "0.78rem" }}>Actual shift duration:</div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input type="number" min={0} max={24} value={durationEdit.hours} onChange={e => setDurationEdit(prev => ({ ...prev, hours: Math.max(0, parseInt(e.target.value) || 0) }))}
                style={{ width: "50px", background: BRAND.raised, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "white", fontSize: "0.9rem", padding: "0.4rem", textAlign: "center" }} />
              <span style={{ color: BRAND.dim, fontSize: "0.8rem" }}>h</span>
              <input type="number" min={0} max={59} value={durationEdit.minutes} onChange={e => setDurationEdit(prev => ({ ...prev, minutes: Math.min(59, Math.max(0, parseInt(e.target.value) || 0)) }))}
                style={{ width: "50px", background: BRAND.raised, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "white", fontSize: "0.9rem", padding: "0.4rem", textAlign: "center" }} />
              <span style={{ color: BRAND.dim, fontSize: "0.8rem" }}>m</span>
              <button onClick={async () => {
                setDurationSaving(true);
                const correctedMs = durationEdit.hours * 3600000 + durationEdit.minutes * 60000;
                try {
                  await updateDoc(doc(db, "shift_summaries", shiftRecap.shiftId), { durationMs: correctedMs, durationCorrected: true });
                  setShiftRecap(prev => ({ ...prev, durationMs: correctedMs }));
                  setDurationSaved(true);
                } catch (e) { console.error(e); }
                setDurationSaving(false);
              }} disabled={durationSaving}
                style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "6px", padding: "0.4rem 0.75rem", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", marginLeft: "auto" }}>
                {durationSaving ? "Saving\u2026" : "Save"}
              </button>
              <button onClick={() => setDurationEdit(null)}
                style={{ background: "none", border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: BRAND.dim, padding: "0.4rem 0.5rem", fontSize: "0.8rem", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {durationSaved && (
          <div style={{ color: BRAND.green, fontSize: "0.78rem" }}>✓ Duration corrected</div>
        )}

        <div style={{ background: BRAND.card, borderRadius: "12px", padding: "1rem 1.5rem", width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ color: "white", fontWeight: "bold", fontSize: "1.05rem", textAlign: "center" }}>
            {shiftRecap.totalOrders} pancake{shiftRecap.totalOrders !== 1 ? "s" : ""}
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: "1.5rem" }}>
            {shiftRecap.flashCount > 0  && <span style={{ color: BRAND.gold,  fontSize: "0.85rem" }}>⚡ {shiftRecap.flashCount} Flash</span>}
            {shiftRecap.customCount > 0 && <span style={{ color: BRAND.sky,   fontSize: "0.85rem" }}>🎨 {shiftRecap.customCount} Custom</span>}
          </div>

          {multiArtist && (
            <div style={{ marginTop: "0.5rem", borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.5rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              {Object.entries(shiftRecap.artistStats).map(([name, a]) => (
                <div key={name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: BRAND.muted, fontSize: "0.82rem" }}>{name}</span>
                  <span style={{ color: BRAND.dim, fontSize: "0.78rem", fontFamily: "monospace" }}>
                    {a.totalOrders} orders
                    {a.bestCustomDrawMs != null ? `  ✏️ ${fmtMs(a.bestCustomDrawMs)}` : ""}
                    {a.bestFlashDrawMs  != null ? `  ⚡ ${fmtMs(a.bestFlashDrawMs)}`  : ""}
                  </span>
                </div>
              ))}
            </div>
          )}

          {shiftRecap.bestDrawMs != null && !multiArtist && (
            <div style={{ textAlign: "center", color: BRAND.dim, fontSize: "0.78rem", fontFamily: "monospace", marginTop: "0.25rem" }}>
              best draw ✏️ {fmtMs(shiftRecap.bestDrawMs)}
            </div>
          )}
        </div>

        {shiftRecap.reviewOrders?.length > 0 && !showReview && (
          <button onClick={() => setShowReview(true)} style={{ ...btnStyle, width: "100%", background: BRAND.sky, color: "white", marginBottom: "0.5rem" }}>
            🎨 Review Pancakes ({shiftRecap.reviewOrders.length})
          </button>
        )}

        {showReview && shiftRecap.reviewOrders?.length > 0 && (
          <ShiftReviewFlow orders={shiftRecap.reviewOrders} onDone={() => setShowReview(false)} />
        )}

        {!showReview && (
          <div style={{ display: "flex", gap: "0.75rem", width: "100%" }}>
            <button onClick={() => setShiftRecap(null)} style={{ ...btnStyle, flex: 1, background: BRAND.raised, color: BRAND.muted }}>Done</button>
            <button onClick={() => { setShiftRecap(null); openShift(); }} disabled={opening}
              style={{ ...btnStyle, flex: 2, background: BRAND.green, color: "white" }}>
              {opening ? "Opening..." : "▶ Open New Queue"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── NO ACTIVE SHIFT — open shift form ──────────────────────────────────────
  if (!activeShift) {
    return (
      <div style={{ padding: "2.5rem 1.5rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1.5rem", maxWidth: "420px", margin: "0 auto" }}>
        <div style={{ fontSize: "3rem" }}>🥞</div>
        <div style={{ color: "white", fontSize: "1.25rem", fontWeight: "bold", textAlign: "center" }}>
          Queue is closed
        </div>

        {/* Event Config staging banner — the form below was pre-filled from
            the /event-config "Apply for next shift" staging. Informational;
            every field stays editable. */}
        {eventSetupApplied && (
          <div style={{
            width: "100%", boxSizing: "border-box",
            background: "rgba(252,179,31,0.1)",
            border: `1px solid ${BRAND.gold}`,
            borderRadius: "10px", padding: "0.55rem 0.85rem",
            color: BRAND.gold, fontSize: "0.8rem", lineHeight: 1.4,
          }}>
            🎪 Loaded from Event Config{eventSetupApplied.profileName ? `: ${eventSetupApplied.profileName}` : ""} — review below, then open the queue.
          </div>
        )}

        {/* Today's booking suggestion — appears when the CRM has a public
            event scheduled for today. Pre-fills event name + address in one
            tap and links the shift to the booking for downstream surfaces
            (CRM booking detail "View shift", /events past tail, etc). */}
        {todayBooking && !dismissedBooking && !linkedBookingId && (
          <div style={{
            width: "100%", boxSizing: "border-box",
            background: "rgba(74,164,225,0.1)",
            border: `1px solid ${BRAND.sky}`,
            borderRadius: "12px", padding: "0.75rem 0.9rem",
            display: "flex", alignItems: "center", gap: "0.7rem",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                <span style={{ color: BRAND.sky, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  📅 On your calendar today
                </span>
                <span style={{
                  fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                  padding: "1px 6px", borderRadius: "999px", lineHeight: 1.5,
                  color: todayBooking.isPublicEvent ? BRAND.gold : BRAND.dim,
                  border: `1px solid ${todayBooking.isPublicEvent ? BRAND.gold : BRAND.border}`,
                  background: todayBooking.isPublicEvent ? "rgba(252,179,31,0.12)" : "transparent",
                }}>
                  {todayBooking.isPublicEvent ? "Public" : "Private"}
                </span>
              </div>
              <div style={{ color: "white", fontSize: "0.92rem", fontWeight: 700, marginTop: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {todayBooking.eventName || "Untitled event"}
              </div>
              {(() => {
                const s = todayBooking.confirmedStart?.toMillis?.();
                const e = todayBooking.confirmedEnd?.toMillis?.();
                if (!s && todayBookingCount <= 1) return null;
                const fmt = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                const timeText = s ? (e ? `${fmt(s)} – ${fmt(e)}` : `Starts ${fmt(s)}`) : null;
                return (
                  <div style={{ color: BRAND.dim, fontSize: "0.72rem", marginTop: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {timeText}
                    {todayBookingCount > 1 && (
                      <span style={{ color: BRAND.gold, marginLeft: timeText ? "0.5rem" : 0 }}>
                        +{todayBookingCount - 1} other today
                      </span>
                    )}
                  </div>
                );
              })()}
              {(todayBooking.venueName || todayBooking.venueAddress) && (
                <div style={{ color: BRAND.dim, fontSize: "0.74rem", marginTop: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {todayBooking.venueName || todayBooking.venueAddress}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={prefillFromTodayBooking}
              style={{ background: BRAND.sky, border: "none", color: "white", borderRadius: "999px", padding: "0.4rem 0.85rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}
            >
              Pre-fill
            </button>
            <button
              type="button"
              onClick={() => setDismissedBooking(true)}
              aria-label="Dismiss"
              style={{ background: "transparent", border: `1px solid ${BRAND.border}`, color: BRAND.dim, width: "28px", height: "28px", borderRadius: "999px", fontSize: "0.9rem", cursor: "pointer", lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        )}

        {/* Linked-to-booking confirmation — shown after Pre-fill, with an
            unlink option in case it was tapped by accident. */}
        {linkedBookingId && (
          <div style={{
            width: "100%", boxSizing: "border-box",
            background: "rgba(112,190,68,0.1)",
            border: `1px solid ${BRAND.green}`,
            borderRadius: "10px", padding: "0.55rem 0.85rem",
            display: "flex", alignItems: "center", gap: "0.6rem",
            fontSize: "0.8rem",
          }}>
            <span style={{ color: BRAND.green, fontWeight: 700 }}>✓ Linked to today's event</span>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => { setLinkedBookingId(null); setDismissedBooking(true); }}
              style={{ background: "transparent", border: "none", color: BRAND.dim, fontSize: "0.75rem", cursor: "pointer", textDecoration: "underline" }}
            >
              Unlink
            </button>
          </div>
        )}

        {/* Event name */}
        <input
          value={eventName}
          onChange={e => setEventName(e.target.value)}
          placeholder="Event name (e.g. Tower Grove Farmers Market)"
          style={{ width: "100%", boxSizing: "border-box", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "10px", color: "white", fontSize: "0.9rem", padding: "0.65rem 0.9rem" }}
        />

        {/* Booth code — the handle guests type and OBS browser sources carry
            (?s=CODE). Set a fixed one per rig so its scene collection can be
            configured once, ahead of the shift. Blank = fresh code each open,
            which is fine for a solo market where nothing is ambiguous. */}
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {/* Tap, don't type: this value is sticky, so a typo made once
              silently persists into every shift this device opens. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            {BOOTH_PRESETS.map(b => {
              const on = fixedJoinCode === b.code;
              return (
                <button
                  key={b.code}
                  type="button"
                  onClick={() => setFixedJoinCode(on ? "" : b.code)}
                  style={{
                    padding: "0.45rem 0.75rem", borderRadius: "999px",
                    background: on ? "rgba(252,179,31,0.2)" : "transparent",
                    border: `1px solid ${on ? BRAND.gold : BRAND.border}`,
                    color: on ? BRAND.gold : BRAND.dim,
                    fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
                  }}
                >{on ? "✓ " : ""}{b.label}</button>
              );
            })}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <input
              value={fixedJoinCode}
              onChange={e => setFixedJoinCode(e.target.value)}
              placeholder="AUTO"
              maxLength={4}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{ width: "6.5rem", flexShrink: 0, boxSizing: "border-box", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "10px", color: "white", fontFamily: "monospace", fontSize: "1rem", letterSpacing: "0.25em", textAlign: "center", padding: "0.65rem 0.5rem" }}
            />
            <div style={{ color: BRAND.dim, fontSize: "0.72rem", lineHeight: 1.35, minWidth: 0 }}>
              Which station this device opens shifts for — only matters when two
              events run at once.
              {fixedJoinCode.length === 4
                ? <> {presetLabelFor(fixedJoinCode) || "Custom"} · overlay URLs use <span style={{ color: BRAND.gold, fontFamily: "monospace" }}>?s={fixedJoinCode}</span></>
                : <> Leave blank to generate one at open.</>}
            </div>
          </div>
        </div>

        {/* Venue address + privacy — optional. Autocomplete is biased toward
            the St. Louis metro so the common case (Tower Grove, St. Charles,
            Belleville) surfaces first with one or two keystrokes. */}
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <AddressAutocomplete
            value={venueAddress}
            onChange={setVenueAddress}
            placeholder="Venue address (optional)"
            inputStyle={{ width: "100%", boxSizing: "border-box", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "10px", color: "white", fontSize: "0.9rem", padding: "0.65rem 0.9rem" }}
          />
          {venueAddress.trim() && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0 0.2rem" }}>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(venueAddress.trim())}`}
                target="_blank" rel="noopener noreferrer"
                style={{ color: BRAND.sky, fontSize: "0.78rem", textDecoration: "none", fontWeight: 600 }}
              >
                📍 Preview on Maps
              </a>
            </div>
          )}
          <label style={{
            display: "flex", alignItems: "center", gap: "0.55rem",
            background: BRAND.card, border: `1px solid ${BRAND.border}`,
            borderRadius: "10px", padding: "0.5rem 0.75rem",
            cursor: "pointer", userSelect: "none", opacity: venueAddress.trim() ? 1 : 0.55,
          }}>
            <input
              type="checkbox"
              checked={!venueIsPublic}
              onChange={e => setVenueIsPublic(!e.target.checked)}
              disabled={!venueAddress.trim()}
              style={{ accentColor: BRAND.gold, cursor: venueAddress.trim() ? "pointer" : "default" }}
            />
            <div>
              <div style={{ color: "white", fontSize: "0.82rem", fontWeight: 600 }}>Keep this venue private</div>
              <div style={{ color: BRAND.dim, fontSize: "0.72rem", marginTop: "2px" }}>
                Hides the address from keepsake pages and the public site. Staff-only.
              </div>
            </div>
          </label>
        </div>

        {/* Artist roster */}
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ color: BRAND.dim, fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Artists this shift</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            {artists.map(name => (
              <div key={name} style={{ display: "flex", alignItems: "center", gap: "0.25rem", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.25rem 0.4rem 0.25rem 0.65rem", fontSize: "0.82rem" }}>
                <span style={{ color: "rgba(255,255,255,0.7)" }}>{name}</span>
                {artists.length > 1 && (
                  <button onClick={() => removeArtist(name)}
                    style={{ background: "none", border: "none", color: BRAND.dim, cursor: "pointer", fontSize: "0.9rem", lineHeight: 1, padding: "0 0.1rem" }}>×</button>
                )}
              </div>
            ))}
          </div>
          {/* Registry quick-picks — one tap adds a known artist to the roster */}
          {registry.filter(a => !artists.includes(a.name)).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
              {registry.filter(a => !artists.includes(a.name)).map(a => (
                <button key={a.id} onClick={() => addArtist(a.name)}
                  style={{ background: "transparent", border: `1px dashed ${BRAND.border}`, borderRadius: "12px", color: BRAND.dim, fontSize: "0.8rem", padding: "0.25rem 0.65rem", cursor: "pointer" }}>
                  + {a.emoji ? `${a.emoji} ` : ""}{a.name}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              value={newArtistInput}
              onChange={e => setNewArtistInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addArtist()}
              placeholder="Add artist…"
              style={{ flex: 1, background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.85rem", padding: "0.45rem 0.7rem" }}
            />
            <button onClick={addArtist}
              style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.85rem", padding: "0.45rem 0.75rem", cursor: "pointer" }}>
              + Add
            </button>
          </div>
        </div>

        {/* Context mode — who's watching this overlay? */}
        <div style={{
          width: "100%", boxSizing: "border-box",
          background: BRAND.card, border: `1px solid ${BRAND.border}`,
          borderRadius: "10px", padding: "0.7rem 0.9rem",
          display: "flex", flexDirection: "column", gap: "0.5rem",
        }}>
          <div style={{ color: "white", fontSize: "0.9rem", fontWeight: "bold" }}>
            🎭 Overlay Context
          </div>
          <div style={{ color: BRAND.dim, fontSize: "0.75rem", lineHeight: 1.4 }}>
            Who's reading the overlay? Picks the header layout and which marquee messages rotate.
          </div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            {[
              { id: "stream", label: "📺 Stream", hint: "broadcast only" },
              { id: "booth",  label: "🏪 Booth",  hint: "in-person only" },
              { id: "both",   label: "🎪 Both",   hint: "streaming from the booth" },
            ].map((opt) => {
              const active = contextMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setContextMode(opt.id)}
                  style={{
                    flex: 1, minWidth: "100px",
                    background: active ? BRAND.gold : BRAND.panel,
                    color: active ? BRAND.dark : BRAND.muted,
                    border: `1px solid ${active ? BRAND.gold : BRAND.border}`,
                    borderRadius: "8px",
                    padding: "0.55rem 0.6rem",
                    cursor: "pointer", textAlign: "center",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: "2px",
                  }}
                >
                  <div style={{ fontSize: "0.85rem", fontWeight: "bold" }}>{opt.label}</div>
                  <div style={{ fontSize: "0.65rem", opacity: 0.85, fontWeight: 600 }}>{opt.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Per-shift overlay messaging — quick toggles to suppress marquee
            lines or booth spotlight cards that don't fit today's venue.
            Lives here (next to Context Mode) so venue-aware decisions happen
            in one moment, without diving into the Stream Setup admin panel. */}
        <ShiftMessagingCard
          marqueeMessages={streamConfig?.marqueeMessages || []}
          showcaseItems={showcaseConfig?.items || []}
          contextMode={contextMode}
          disabledMarqueeTexts={pendingDisabledMarqueeTexts}
          disabledShowcaseIds={pendingDisabledShowcaseIds}
          onToggleMarquee={togglePendingMarquee}
          onToggleShowcase={togglePendingShowcase}
        />

        {/* Flash design sets — picker for which curated bundles to activate
            on this shift, in addition to (or instead of) the base IP set.
            Empty selection + base ON = today's existing behavior. */}
        <div style={{
          width: "100%", boxSizing: "border-box",
          background: BRAND.card, border: `1px solid ${BRAND.border}`,
          borderRadius: "10px", padding: "0.7rem 0.9rem",
          display: "flex", flexDirection: "column", gap: "0.55rem",
        }}>
          <div style={{ color: "white", fontSize: "0.9rem", fontWeight: "bold" }}>
            📚 Flash design sets
          </div>
          <div style={{ color: BRAND.dim, fontSize: "0.75rem", lineHeight: 1.4 }}>
            Curated bundles activated on this shift. Leave empty for the standard rotation.
            Manage sets at <span style={{ color: BRAND.sky }}>Setup → Flash Sets</span>.
          </div>

          {allProfiles == null ? (
            <div style={{ color: BRAND.dim, fontSize: "0.78rem", fontStyle: "italic" }}>Loading…</div>
          ) : allProfiles.length === 0 ? (
            <div style={{ color: BRAND.dim, fontSize: "0.78rem", fontStyle: "italic" }}>
              No sets defined yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              {allProfiles.map(p => {
                const checked = selectedProfileIds.includes(p.id);
                return (
                  <label key={p.id} style={{
                    display: "flex", alignItems: "center", gap: "0.6rem",
                    background: checked ? "rgba(252,179,31,0.08)" : BRAND.panel,
                    border: `1px solid ${checked ? BRAND.gold : BRAND.border}`,
                    borderRadius: "8px", padding: "0.45rem 0.65rem",
                    cursor: "pointer", userSelect: "none",
                  }}>
                    <input
                      type="checkbox" checked={checked}
                      onChange={e => {
                        setSelectedProfileIds(prev =>
                          e.target.checked
                            ? [...prev, p.id]
                            : prev.filter(id => id !== p.id)
                        );
                      }}
                      style={{ accentColor: BRAND.gold, cursor: "pointer" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "white", fontSize: "0.85rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.name || "Untitled set"}
                      </div>
                      <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
                        {(p.designs || []).length} design{(p.designs || []).length === 1 ? "" : "s"}
                        {p.clientName ? ` · ${p.clientName}` : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          <label style={{
            display: "flex", alignItems: "center", gap: "0.55rem",
            background: BRAND.panel, border: `1px solid ${includeBaseFlashSet ? BRAND.sky : BRAND.border}`,
            borderRadius: "8px", padding: "0.45rem 0.65rem",
            cursor: "pointer", userSelect: "none",
          }}>
            <input
              type="checkbox" checked={includeBaseFlashSet}
              onChange={e => setIncludeBaseFlashSet(e.target.checked)}
              style={{ accentColor: BRAND.sky, cursor: "pointer" }}
            />
            <div style={{ color: includeBaseFlashSet ? BRAND.sky : BRAND.muted, fontSize: "0.82rem", fontWeight: 600 }}>
              Include the standard IP set
            </div>
          </label>
        </div>

        {/* Ordering voice — seeds shift.qrFlows.mode so a party shift opens
            in party voice (scans go straight into the queue, no standby). */}
        <div style={{
          width: "100%", boxSizing: "border-box",
          background: BRAND.card,
          border: `1px solid ${joinModeDefault !== "off" ? (joinModeDefault === "free" ? BRAND.pink : BRAND.gold) : BRAND.border}`,
          borderRadius: "10px", padding: "0.65rem 0.9rem",
        }}>
          <div style={{ color: "white", fontSize: "0.9rem", fontWeight: "bold", marginBottom: "0.5rem" }}>
            📲 QR / Stream Ordering (/join)
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {[
              { id: "off",  label: "🚫 Off",    tint: BRAND.muted },
              { id: "free", label: "🎈 Party",  tint: BRAND.pink },
              { id: "paid", label: "🎟 Market", tint: BRAND.gold },
            ].map(opt => {
              const active = joinModeDefault === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setJoinModeDefault(opt.id)}
                  style={{
                    flex: 1, cursor: "pointer",
                    background: active ? "rgba(255,255,255,0.08)" : BRAND.panel,
                    border: `2px solid ${active ? opt.tint : BRAND.border}`,
                    borderRadius: "8px", padding: "0.55rem 0.4rem",
                    color: active ? opt.tint : BRAND.dim,
                    fontSize: "0.85rem", fontWeight: 700,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <div style={{ color: BRAND.dim, fontSize: "0.75rem", marginTop: "0.5rem", lineHeight: 1.4 }}>
            {joinModeDefault === "free"
              ? "Client-paid event — scans go straight into the queue with a number. No prices anywhere."
              : joinModeDefault === "paid"
                ? `Public market — tier picker (⚡ ${fmtCents(pendingPricing?.flashCents ?? 1000)} · 📷 ${fmtCents(pendingPricing?.customCents ?? 3000)}${pendingPricing?.hideStandby ? "" : " · 🕐 standby free"}). Standby waits for payment or your promote.`
                : "No ordering QR on any screen; /join is closed."}
          </div>
        </div>

        {/* 🧳 Road Mode pricing — staged by Event Config Apply. Read-only here
            (the profile is the editor); ✕ opens the shift on home prices. */}
        {pendingPricing && (
          <div style={{
            background: "rgba(252,179,31,0.08)", border: `1px solid ${BRAND.gold}`,
            borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "1rem",
            display: "flex", alignItems: "flex-start", gap: "0.6rem",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: BRAND.gold, fontSize: "0.84rem", fontWeight: 700 }}>
                🧳 Event pricing{pendingPricing.eventTag ? ` — ${pendingPricing.eventTag}` : ""}
              </div>
              <div style={{ color: BRAND.muted, fontSize: "0.72rem", lineHeight: 1.45, marginTop: "0.15rem" }}>
                ⚡ {fmtCents(pendingPricing.flashCents ?? 1000)} · 📷 {fmtCents(pendingPricing.customCents ?? 3000)}
                {(pendingPricing.extras || []).map((e) => ` · ${e.emoji || "✨"} +${fmtCents(e.cents)}`).join("")}
                {pendingPricing.taxPct ? ` · +${pendingPricing.taxPct}% tax` : ""} · tipping on
                <br />Reverts automatically when this shift closes.
              </div>
            </div>
            <button type="button" onClick={() => setPendingPricing(null)} title="Open on home prices instead" style={{
              background: "none", border: `1px solid ${BRAND.border}`, borderRadius: "6px",
              color: BRAND.dim, fontSize: "0.72rem", padding: "0.2rem 0.45rem", cursor: "pointer", flexShrink: 0,
            }}>✕</button>
          </div>
        )}

        {/* 🎁 Combo offers for this shift — parity with 🎛 Shift Controls so
            the upsell mix is settable BEFORE opening, not only mid-shift. */}
        <div style={{
          background: BRAND.panel, border: `1px solid ${BRAND.border}`,
          borderRadius: "10px", padding: "0.6rem 0.75rem", marginBottom: "1rem",
        }}>
          <div style={{ color: "white", fontSize: "0.9rem", fontWeight: "bold", marginBottom: "0.5rem" }}>
            🎁 Combo offers this shift
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {COMBO_ORDER.map((key) => {
              const on = pendingComboKeys.includes(key);
              const combo = COMBOS[key];
              return (
                <button key={key} type="button"
                  onClick={() => setPendingComboKeys((prev) =>
                    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])}
                  style={{
                    flex: 1, minWidth: "130px", cursor: "pointer",
                    background: on ? "rgba(252,179,31,0.1)" : BRAND.panel,
                    border: `2px solid ${on ? BRAND.gold : BRAND.border}`,
                    borderRadius: "8px", padding: "0.5rem 0.5rem",
                    color: on ? BRAND.gold : BRAND.dim,
                    fontSize: "0.8rem", fontWeight: 700,
                  }}>
                  {on ? "✓ " : ""}{combo.emoji} {combo.label} — {fmtCents(pendingPricing?.combos?.[key] ?? combo.priceCents)}
                </button>
              );
            })}
          </div>
          <div style={{ color: BRAND.dim, fontSize: "0.7rem", marginTop: "0.4rem", lineHeight: 1.35 }}>
            Party voice hides combos automatically. Flip these anytime in 🎛 Shift Controls.
          </div>
        </div>

        {/* 🦋 Bluesky griddle bot — sticky, default off */}
        <label style={{
          display: "flex", alignItems: "center", gap: "0.55rem",
          background: BRAND.panel, border: `1px solid ${blueskyAutoPost ? BRAND.sky : BRAND.border}`,
          borderRadius: "8px", padding: "0.45rem 0.65rem", marginBottom: "1rem",
          cursor: "pointer", userSelect: "none",
        }}>
          <input
            type="checkbox" checked={blueskyAutoPost}
            onChange={e => setBlueskyAutoPost(e.target.checked)}
            style={{ accentColor: BRAND.sky, cursor: "pointer" }}
          />
          <div>
            <div style={{ color: blueskyAutoPost ? BRAND.sky : BRAND.muted, fontSize: "0.82rem", fontWeight: 600 }}>
              🦋 Live-post pancakes to Bluesky
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.7rem", lineHeight: 1.35 }}>
              One thread per shift on @thejoyofpancakes.com — flip anytime in 🎛 Shift Controls.
            </div>
          </div>
        </label>

        <button
          onClick={openShift}
          disabled={opening || !eventName.trim()}
          title={!eventName.trim() ? "Enter an event name first" : undefined}
          style={{ ...btnStyle, background: eventName.trim() ? BRAND.green : BRAND.panel, color: eventName.trim() ? "white" : BRAND.dim, width: "100%", fontSize: "1.1rem", cursor: eventName.trim() ? "pointer" : "not-allowed" }}
        >
          {opening ? "Opening..." : "▶ Open Queue"}
        </button>

        {/* Divider + content session shortcut */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", width: "100%", margin: "0.2rem 0" }}>
          <div style={{ flex: 1, height: "1px", background: BRAND.divider }} />
          <div style={{ color: BRAND.dim, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>or</div>
          <div style={{ flex: 1, height: "1px", background: BRAND.divider }} />
        </div>
        <button
          onClick={openContentSession}
          disabled={opening}
          style={{ ...btnStyle, background: BRAND.raised, color: "white", width: "100%", fontSize: "0.95rem", border: `1px solid ${BRAND.border}` }}
          title="Auto-named, no event/venue. For video shoots + livestream-only sessions."
        >
          {opening ? "Opening..." : "🎬 Start content session"}
        </button>
      </div>
    );
  }

  // ── CLOSE CONFIRMATION MODAL ───────────────────────────────────────────────
  if (showCloseConfirm) {
    return (
      <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* Combo goods still owed — paid-for kits/prints that never changed
            hands. Warns, never blocks: the trail survives in the archive, but
            catching it before teardown is the whole point. */}
        {pendingAddOnOrders.length > 0 && (
          <div style={{ background: "rgba(252,179,31,0.1)", border: `1px solid ${BRAND.gold}`, borderRadius: "10px", padding: "0.75rem" }}>
            <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem", marginBottom: "0.4rem" }}>
              🎁 Combo goods not handed over yet
            </div>
            <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>
              {pendingAddOnOrders.map(o => (
                <div key={o.id} style={{ marginBottom: "0.25rem" }}>
                  #{o.queueNumber ?? "—"} — {o.recipientName || o.subjectName || "Guest"}: {unfulfilledAddOns(o).map(addOnLabel).join(" · ")}
                </div>
              ))}
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.75rem", marginTop: "0.35rem" }}>
              Hand them over and tap the badges off in the queue's Completed drawer — or close anyway and settle up from the shift archive.
            </div>
          </div>
        )}
        {pendingOrders.length > 0 ? (
          <>
            <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem" }}>
              ⚠ {pendingOrders.length} unresolved order{pendingOrders.length > 1 ? "s" : ""} in queue
            </div>
            <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>
              {pendingOrders.map(o => (
                <div key={o.id} style={{ marginBottom: "0.25rem" }}>
                  #{o.queueNumber} — {o.recipientName} ({o.state})
                </div>
              ))}
            </div>
            <div style={{ color: "white", fontSize: "0.9rem" }}>
              Resolve these orders first, or use Force Close to reject them all with an "event ended" note.
            </div>
            <button onClick={() => closeShift(true)} disabled={closing}
              style={{ ...btnStyle, background: BRAND.red }}>
              {closing ? "Closing..." : "⚠ Force Close & Reject All"}
            </button>
            <button onClick={() => setShowCloseConfirm(false)} style={{ ...btnStyle, background: BRAND.card }}>
              Go Back
            </button>
          </>
        ) : (
          <>
            <div style={{ color: "white", fontSize: "1rem", textAlign: "center" }}>
              Close the shift and generate today's summary?
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.8rem", textAlign: "center" }}>Shift duration: {elapsed}</div>
            <button onClick={() => closeShift(false)} disabled={closing}
              style={{ ...btnStyle, background: BRAND.orange }}>
              {closing ? "Closing..." : "Close Shift"}
            </button>
            <button onClick={() => setShowCloseConfirm(false)} style={{ ...btnStyle, background: BRAND.card }}>
              Cancel
            </button>
          </>
        )}
      </div>
    );
  }

  // ── AUTO-CLOSE PROMPT ─────────────────────────────────────────────────────
  // Shown after 4hr inactivity — gives artist the choice to close or keep going.
  if (showAutoClosePrompt) {
    const inactiveHours = activeShift?.lastActivityAt?.toMillis
      ? Math.round((Date.now() - activeShift.lastActivityAt.toMillis()) / 3600000)
      : "4+";
    return (
      <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "420px", margin: "0 auto" }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem", textAlign: "center" }}>
          ⏰ Shift still open
        </div>
        <div style={{ color: BRAND.muted, fontSize: "0.9rem", textAlign: "center", lineHeight: 1.5 }}>
          No queue activity for ~{inactiveHours} hours. Did you forget to close this shift?
        </div>
        <button onClick={() => { setShowAutoClosePrompt(false); beginCloseShift(); }}
          style={{ ...btnStyle, background: BRAND.orange, color: "white", width: "100%", fontSize: "1rem" }}>
          Close Shift Now
        </button>
        <button onClick={() => { setShowAutoClosePrompt(false); }}
          style={{ ...btnStyle, background: BRAND.card, color: BRAND.muted, border: `1px solid ${BRAND.border}`, width: "100%", fontSize: "0.9rem" }}>
          Keep It Open
        </button>
      </div>
    );
  }

  // ── ACTIVE SHIFT HUD ───────────────────────────────────────────────────────
  // flexWrap + rowGap so the five controls reflow onto a second line on
  // narrow viewports (iPhone 14, 390px) instead of pushing Close Shift
  // off-screen to the right. justifyContent keeps the buttons right-aligned
  // so they feel balanced against the left-aligned title in the parent bar.
  // The 📣 Messaging button glows gold whenever this shift overrides any
  // default — a suppressed marquee/showcase item OR a non-default flash config.
  const messagingOffCount =
    (activeShift.disabledMarqueeTexts?.length || 0) + (activeShift.disabledShowcaseIds?.length || 0);
  const flashOverridden =
    (activeShift.activeFlashProfileIds?.length || 0) > 0 || activeShift.includeBaseFlashSet === false;
  const messagingDirty = messagingOffCount > 0 || flashOverridden;
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      gap: "0.5rem",
      rowGap: "0.4rem",
    }}>
      <div style={{ color: BRAND.dim, fontSize: "0.75rem", fontFamily: "monospace", letterSpacing: "0.05em" }}>
        {elapsed}
      </div>

      {activeShift.isQueueOpen ? (
        <button onClick={closeQueue} style={{ ...hudBtn, borderColor: BRAND.orange, color: BRAND.orange }}>
          No More Orders
        </button>
      ) : (
        <button onClick={reopenQueue} style={{ ...hudBtn, borderColor: BRAND.green, color: BRAND.green }}>
          Re-open Queue
        </button>
      )}

      {/* 🎛 Shift Controls — ordering flows (shift.qrFlows) + scene context.
          Chip shows the live ordering mode at a glance. */}
      {/* Chip is tinted per voice (party pink / market gold) so a glance tells
          you which lane scans are landing in — the two labels used to render
          in the same sky color and read identical at booth distance. */}
      {(() => {
        const m = activeShift.qrFlows?.mode;
        const tint =
          m === "free" ? BRAND.pink
          : m === "paid" ? BRAND.gold
          : m === "custom" ? BRAND.sky
          : m === "off" ? BRAND.border
          : activeShift.livestreamJoinEnabled ? BRAND.sky : BRAND.border;
        const label =
          m === "free" ? "🎈 Party"
          : m === "paid" ? "🎟 Market"
          : m === "custom" ? "🔗 Custom"
          : m === "off" ? "Ordering off"
          : activeShift.livestreamJoinEnabled ? "Ordering on" : "Controls";
        return (
          <button
            onClick={() => setShowControlsModal(true)}
            title="On-screen ordering flows + scene context"
            style={{
              ...hudBtn,
              borderColor: tint,
              color: m === "off" || (!m && !activeShift.livestreamJoinEnabled) ? BRAND.muted : tint,
              fontWeight: 700,
            }}
          >
            🎛 {label}
          </button>
        );
      })()}

      <button
        onClick={() => {
          writeAlert("tip_received", {}, { message: "💛 Thanks for the tip! You're amazing." }).catch(() => {});
        }}
        style={{ ...hudBtn, borderColor: BRAND.gold, color: BRAND.gold }}
      >
        🫙 Tip
      </button>

      <button
        onClick={() => setShowMessagingModal(true)}
        title="Marquee lines, booth spotlight cards, and flash design sets for this shift"
        style={{
          ...hudBtn,
          borderColor: messagingDirty ? BRAND.gold : BRAND.border,
          color: messagingDirty ? BRAND.gold : BRAND.muted,
        }}
      >
        📣 Messaging
        {messagingOffCount > 0 && (
          <span style={{ marginLeft: "0.3rem", opacity: 0.85 }}>
            ({messagingOffCount} off)
          </span>
        )}
      </button>

      <button onClick={beginCloseShift} style={{ ...hudBtn, border: `1px solid ${BRAND.border}`, color: BRAND.muted }}>
        Close Shift
      </button>

      {/* 🎛 Shift Controls modal — live edits write straight to Firestore.
          Same interaction family as the Messaging modal below. */}
      {showControlsModal && (
        <div
          onClick={() => setShowControlsModal(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9998,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            padding: "5vh 1rem 1rem",
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: "440px",
              background: BRAND.bg,
              border: `1px solid ${BRAND.border}`,
              borderRadius: "14px",
              padding: "1rem",
              display: "flex", flexDirection: "column", gap: "0.8rem",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
              <div style={{ color: "white", fontSize: "1rem", fontWeight: 700 }}>
                Shift controls
              </div>
              <button
                onClick={() => setShowControlsModal(false)}
                aria-label="Close"
                style={{
                  background: "transparent", border: `1px solid ${BRAND.border}`,
                  color: BRAND.muted, width: "32px", height: "32px",
                  borderRadius: "999px", fontSize: "1rem", cursor: "pointer", lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <ShiftOrderingControls
              shift={activeShift}
              contextMode={activeShift.contextMode || "stream"}
              onWrite={async (patch) => {
                const wasJoinOn = !!activeShift.livestreamJoinEnabled;
                const wasFree = activeShift.qrFlows?.mode === "free";
                await updateDoc(doc(db, "shifts", activeShift.id), patch);
                // Sticky default for the next shift's open form + the "/join
                // is live" toast when the gate flips on.
                const nextMode = patch.qrFlows?.mode;
                if (nextMode === "off" || nextMode === "free" || nextMode === "paid") {
                  setJoinModeDefault(nextMode);
                }
                // Mid-shift Bluesky flips update the sticky too, so the next
                // open form remembers the last live choice.
                if (typeof patch.blueskyAutoPost === "boolean") {
                  setBlueskyAutoPost(patch.blueskyAutoPost);
                }
                if (typeof patch.livestreamJoinEnabled === "boolean") {
                  if (!wasJoinOn && patch.livestreamJoinEnabled) {
                    setStreamJoinJustEnabled(true);
                    setTimeout(() => setStreamJoinJustEnabled(false), 2400);
                  }
                }
                // Flipping to Party changes the rule for FUTURE scans only —
                // offer to rescue the requests already stranded on standby
                // (they'd otherwise sit until hand-promoted one by one).
                if (nextMode === "free" && !wasFree) {
                  try {
                    const snap = await getDocs(query(
                      collection(db, "orders"),
                      where("shiftId", "==", activeShift.id),
                      where("queue", "==", "standby"),
                      where("state", "==", "queued"),
                    ));
                    const waiting = snap.docs.filter(d => {
                      const o = d.data();
                      return !o.trashedAt && !o.holdIndefinitely;
                    }).length;
                    if (waiting > 0 && window.confirm(
                      `🎈 Party mode is on. Admit the ${waiting} request${waiting === 1 ? "" : "s"} waiting on standby into the queue now?`
                    )) {
                      const fn = httpsCallable(getFunctions(getApp()), "admitAllStandby");
                      await fn({ shiftId: activeShift.id });
                    }
                  } catch (err) {
                    console.warn("Standby admit check failed:", err?.message);
                  }
                }
              }}
              onContextMode={async (next) => {
                await updateDoc(doc(db, "shifts", activeShift.id), { contextMode: next });
                // Mirror to liveShift so overlays following that doc stay in sync.
                setDoc(doc(db, "config", "liveShift"), { contextMode: next }, { merge: true })
                  .catch(err => console.warn("liveShift contextMode update failed:", err?.message));
                setContextMode(next);
              }}
            />
          </div>
        </div>
      )}

      {/* Messaging modal — live edits write straight to Firestore. */}
      {showMessagingModal && (
        <div
          onClick={() => setShowMessagingModal(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9998,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            padding: "5vh 1rem 1rem",
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: "440px",
              background: BRAND.bg,
              border: `1px solid ${BRAND.border}`,
              borderRadius: "14px",
              padding: "1rem",
              display: "flex", flexDirection: "column", gap: "0.8rem",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
              <div style={{ color: "white", fontSize: "1rem", fontWeight: 700 }}>
                Messaging & flash sets
              </div>
              <button
                onClick={() => setShowMessagingModal(false)}
                aria-label="Close"
                style={{
                  background: "transparent", border: `1px solid ${BRAND.border}`,
                  color: BRAND.muted, width: "32px", height: "32px",
                  borderRadius: "999px", fontSize: "1rem", cursor: "pointer", lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <ShiftMessagingCard
              marqueeMessages={streamConfig?.marqueeMessages || []}
              showcaseItems={showcaseConfig?.items || []}
              contextMode={activeShift.contextMode || "stream"}
              disabledMarqueeTexts={activeShift.disabledMarqueeTexts || []}
              disabledShowcaseIds={activeShift.disabledShowcaseIds || []}
              onToggleMarquee={toggleActiveMarquee}
              onToggleShowcase={toggleActiveShowcase}
              intro="Changes apply live to overlays. Resets next shift."
            />

            {/* Flash design sets — live edits write straight to the active
                shift doc. Overlays + register read these via
                useFlashDesigns(activeShift) and re-render within a frame.
                The fix for "can't drop the IP set mid-shift." */}
            <div style={{ height: "1px", background: BRAND.divider, margin: "0.1rem 0" }} />
            <div style={{ color: "white", fontSize: "0.95rem", fontWeight: 700 }}>
              ⚡ Flash design sets
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.78rem", lineHeight: 1.4, marginTop: "-0.4rem" }}>
              Which curated bundles show on overlays + the register this shift.
            </div>

            {allProfiles == null ? (
              <div style={{ color: BRAND.dim, fontSize: "0.78rem", fontStyle: "italic" }}>Loading…</div>
            ) : allProfiles.length === 0 ? (
              <div style={{ color: BRAND.dim, fontSize: "0.78rem", fontStyle: "italic" }}>
                No sets defined yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                {allProfiles.map(p => {
                  const checked = (activeShift.activeFlashProfileIds || []).includes(p.id);
                  return (
                    <label key={p.id} style={{
                      display: "flex", alignItems: "center", gap: "0.6rem",
                      background: checked ? "rgba(252,179,31,0.08)" : BRAND.panel,
                      border: `1px solid ${checked ? BRAND.gold : BRAND.border}`,
                      borderRadius: "8px", padding: "0.45rem 0.65rem",
                      cursor: "pointer", userSelect: "none",
                    }}>
                      <input
                        type="checkbox" checked={checked}
                        onChange={() => toggleActiveFlashProfile(p.id)}
                        style={{ accentColor: BRAND.gold, cursor: "pointer" }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "white", fontSize: "0.85rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p.name || "Untitled set"}
                        </div>
                        <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
                          {(p.designs || []).length} design{(p.designs || []).length === 1 ? "" : "s"}
                          {p.clientName ? ` · ${p.clientName}` : ""}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <label style={{
              display: "flex", alignItems: "center", gap: "0.55rem",
              background: BRAND.panel,
              border: `1px solid ${activeShift.includeBaseFlashSet !== false ? BRAND.sky : BRAND.border}`,
              borderRadius: "8px", padding: "0.45rem 0.65rem",
              cursor: "pointer", userSelect: "none",
            }}>
              <input
                type="checkbox" checked={activeShift.includeBaseFlashSet !== false}
                onChange={e => setActiveBaseFlashSet(e.target.checked)}
                style={{ accentColor: BRAND.sky, cursor: "pointer" }}
              />
              <div style={{ color: activeShift.includeBaseFlashSet !== false ? BRAND.sky : BRAND.muted, fontSize: "0.82rem", fontWeight: 600 }}>
                Include the standard IP set
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Toast — appears briefly when Stream Join is freshly enabled */}
      {streamJoinJustEnabled && (
        <div style={{
          position: "fixed", left: "50%", bottom: "1.5rem", transform: "translateX(-50%)",
          background: BRAND.sky, color: "white",
          padding: "0.75rem 1.25rem", borderRadius: "999px",
          fontSize: "0.95rem", fontWeight: "bold",
          boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
          zIndex: 9999, pointerEvents: "none",
          animation: "jopToastIn 0.3s ease-out",
        }}>
          ✓ /join is now live
        </div>
      )}
    </div>
  );
}

const btnStyle = {
  padding: "0.875rem 1.5rem",
  borderRadius: "10px",
  border: "none",
  fontSize: "0.95rem",
  fontWeight: "bold",
  cursor: "pointer",
};

const hudBtn = {
  background: "transparent",
  border: "1px solid",
  borderRadius: "8px",
  padding: "0.35rem 0.6rem",
  fontSize: "0.7rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
